import type { SagaIterator } from 'redux-saga';
import {
  take,
  put,
  all,
  call,
  delay,
  race,
  fork,
  cancel,
  select,
  SagaReturnType,
} from 'redux-saga/effects';
import {
  getParamContainer,
  getParamLanguage,
  getParamBaseURL,
  getParamBrand,
  getParamPlatform,
  getParamChannel,
  getParamProxyPrefix,
  getParamUserToken,
  getParamDepositUrl,
  getParamUserData,
  getParamClientId,
  SET_PARAM_CONTAINER,
  SET_PARAM_LANGUAGE,
  SET_PARAM_BASE_URL,
  SET_PARAM_BRAND,
  SET_PARAM_CHANNEL,
  SET_PARAM_PROXY_PREFIX,
  SET_PARAM_USER_TOKEN,
  SET_PARAM_DEPOSIT_URL,
  SET_PARAM_USER_DATA,
  SET_PARAM_CLIENT_ID,
  SERVICE_RENDER_ERROR,
  SET_PARAM_PLATFORM,
} from '@corefront/redux-aba';
import { create } from '../../../utils';
import {
  getShellNameByContainer,
  getShellClassName,
} from '../../../utils/shells';
import { RENDER_APP, DESTROY_APP } from '../../constants/initialization';
import { FETCH_CONFIGURATION_FAIL } from '../../constants/configuration';
import { createHistory, resetHistory } from '../../../services/history';
import { publishServiceRenderErrorByTimeout } from '../../actions/service';
import { fetchConfigurationRequest } from '../../actions/app/configuration';
import {
  setIsRendered,
  destroyAppSuccess,
  resetStore,
  setIsBaseParamsSetUp,
} from '../../actions/app/initialization';
import {
  baseURLSelector,
  containerSelector,
  isNativeSelector,
  proxyPrefixSelector,
} from '../../reducers/app/params';
import { initializeLocalization } from '../translations';
import { handleWebpackPublicPath } from '../handleWebpackPublicPath';
import { changeLanguageWatcher } from '../changeLanguage';
import { layoutDimensions } from '../layoutDimensions';
import { authorizationWatcher } from '../authorization';
import { fetchCommonConfig } from '../fetch';
import { SET_IS_CONFIGURATION_LOADED } from '../../constants/initialization';
import { SERVICE_NAME } from '../../../constants';
import { trackFirstOpen } from '../../../utils/ga';
import { checkLocalGameLaunchMode } from '../localGameLauchMode';
import { initializeAPI } from '../api';
import logger from '../../logger';
import { changeNavigationWatcher } from '../changeNavigation';
import { changeUserBalanceWatcher } from '../changeUserBalance';
import { handleABAErrors } from '../handleABAErrors';
import { checkAMSSegments } from '../AMSSegments';
import { growthbookWatcher } from '../growthbookWatcher';
import { analyticsWatcher } from '../analyticsWatcher';
import { resetThemeMode } from '../themeWatcher';

export const RENDER_TIMEOUT = 5000;

/**
 * Request basic params for rendering app and waits for resolve
 * @param {number} timeout - Amount of ms for delay effect
 */
export function* getBaseParamsWithTimeout(
  timeout: number
): SagaIterator<{ timeout: boolean; params: unknown }> {
  // we're publishing events
  yield all([
    put(getParamPlatform()),
    put(getParamContainer()),
    put(getParamLanguage()),
    put(getParamBaseURL()),
    put(getParamBrand()),
    put(getParamChannel()),
    put(getParamProxyPrefix()),
    put(getParamUserToken()),
    put(getParamDepositUrl()),
    put(getParamUserData()),
    put(getParamClientId()),
  ]);

  // then wait for async events from host shell or fail after some delay
  return yield race({
    params: all([
      take(SET_PARAM_PLATFORM),
      take(SET_PARAM_CONTAINER),
      take(SET_PARAM_LANGUAGE),
      take(SET_PARAM_BASE_URL),
      take(SET_PARAM_BRAND),
      take(SET_PARAM_CHANNEL),
      take(SET_PARAM_PROXY_PREFIX),
      take(SET_PARAM_USER_TOKEN),
      take(SET_PARAM_DEPOSIT_URL),
      take(SET_PARAM_USER_DATA),
      take(SET_PARAM_CLIENT_ID),
    ]),
    timeout: delay(timeout),
  });
}

/**
 * Creates global history object
 */
export function* initializeHistory(): SagaIterator<
  SagaReturnType<typeof createHistory>
> {
  const basename: ReturnType<typeof baseURLSelector> = yield select(
    baseURLSelector
  );
  const isNative: ReturnType<typeof isNativeSelector> = yield select(
    isNativeSelector
  );
  const pathname = isNative && getPathname(basename);

  return yield call(createHistory, isNative, { basename, pathname });
}

export function getPathname(basename: string) {
  const locationPathname = window.location.pathname;
  const search = window.location.search ?? '';

  if (basename === '/') return locationPathname;

  return locationPathname.replace(basename, '').concat(search);
}

export function* loadFont() {
  try {
    /* global globalThis */
    if (!('fonts' in document)) {
      return;
    }
    const proxyPrefix: ReturnType<typeof proxyPrefixSelector> = yield select(
      proxyPrefixSelector
    );
    const container: ReturnType<typeof containerSelector> = yield select(
      containerSelector
    );
    const fontPath = process.env.NODE_ENV !== 'development' ? proxyPrefix : '';

    yield call(
      loadFontFace,
      'CasinoRoboto',
      `url(${fontPath}/fonts/Roboto/roboto-regular-webfont.woff2) format('woff2'), url(${fontPath}/fonts/Roboto/roboto-regular-webfont.woff) format('woff')`
    );

    yield call(
      loadFontFace,
      'CasinoRobotoBold',
      `url(${fontPath}/fonts/Roboto/roboto-bold-webfont.woff2) format('woff2'), url(${fontPath}/fonts/Roboto/roboto-bold-webfont.woff) format('woff')`
    );

    yield call(
      loadFontFace,
      'CasinoRobotoExtrabold',
      `url(${fontPath}/fonts/Roboto/roboto-extrabold-webfont.woff2) format('woff2'), url(${fontPath}/fonts/Roboto/roboto-extrabold-webfont.woff) format('woff')`
    );

    yield call(
      loadFontFace,
      'CasinoRobotoMedium',
      `url(${fontPath}/fonts/Roboto/roboto-medium.woff2) format('woff2'), url(${fontPath}/fonts/Roboto/roboto-medium.woff) format('woff')`
    );

    yield call(
      loadFontFace,
      'DrukWide',
      `url(${fontPath}/fonts/DrukWide/DrukWide-BoldItalic-Web.woff2) format('woff2'), url(${fontPath}/fonts/DrukWide/DrukWide-BoldItalic-Web.woff) format('woff')`
    );

    yield call(
      loadFontFace,
      'DrukWideLCGWeb',
      `url(${fontPath}/fonts/DrukWide/DrukWide-BoldItalic-Cy-Gr-Web.woff2) format('woff2'), url(${fontPath}/fonts/DrukWide/DrukWide-BoldItalic-Cy-Gr-Web.woff) format('woff')`
    );

    const DOM_NODE: HTMLDivElement = document.querySelector(container);
    if (DOM_NODE) {
      DOM_NODE.style.fontFamily = 'CasinoRoboto, sans-serif';
    }
  } catch (e) {
    logger.warn('Could not load font. Skipping...');
  }
}

export function* loadFontFace(name: string, url: string) {
  /**
   * Create FontFace instance
   * @link https://developer.mozilla.org/ru/docs/Web/API/FontFace/FontFace
   */
  const font = yield create(globalThis.FontFace, name, url);
  const fontFace = yield call([font, 'load']);
  yield call([globalThis.document.fonts, 'add'], fontFace);
}

export function* getShell() {
  const container: ReturnType<typeof containerSelector> = yield select(
    containerSelector
  );

  // common solution to determine MVP environment
  return getShellNameByContainer(container);
}

export function* setHostClassName() {
  const shell: SagaReturnType<typeof getShell> = yield call(getShell);
  const container: ReturnType<typeof containerSelector> = yield select(
    containerSelector
  );
  const DOM_NODE = document.querySelector(container);

  const shellClassName = getShellClassName(shell);

  if (DOM_NODE) {
    if (shellClassName) {
      DOM_NODE?.classList?.add?.(shellClassName);
    }
  } else {
    logger.warn(
      'Unable to find root DOM node for adding required CSS classes.'
    );
  }
}

/**
 * Try to get all basic params and render application
 */
export function* tryRender() {
  const { timeout }: SagaReturnType<typeof getBaseParamsWithTimeout> =
    yield call(getBaseParamsWithTimeout, RENDER_TIMEOUT);

  if (timeout) {
    return yield put(publishServiceRenderErrorByTimeout());
  }

  yield call(initializeHistory);

  yield put(setIsBaseParamsSetUp(true));

  // initialize EVA API SDK
  yield call(initializeAPI);

  // sync call of remote config
  yield put(fetchConfigurationRequest());

  // wait until request finishes
  yield take(SET_IS_CONFIGURATION_LOADED);

  // checking if ams segments enabled
  yield call(checkAMSSegments);

  // load font
  yield call(loadFont);

  // check AppStore review process
  yield call(checkLocalGameLaunchMode);

  yield call(trackFirstOpen, SERVICE_NAME);

  // render application
  yield put(setIsRendered(true));
}

export function* serviceFlow() {
  yield fork(handleWebpackPublicPath);
  yield fork(changeLanguageWatcher);
  yield fork(changeNavigationWatcher);
  yield fork(handleABAErrors);
  yield fork(growthbookWatcher);
  yield fork(analyticsWatcher);
}

export function* appFlow() {
  yield fork(changeUserBalanceWatcher);
  yield fork(layoutDimensions);
  yield fork(initializeLocalization);
  yield fork(authorizationWatcher);
  yield fork(fetchCommonConfig);
  yield fork(tryRender);
}

export function* renderFlow() {
  while (true) {
    yield take(RENDER_APP);

    const serviceTask = yield fork(serviceFlow);
    const appTask = yield fork(appFlow);

    /**
     * So, there could be only 2 possible solutions
     * 1. successful render after getting all params - and we should wait for DESTROY_APP action
     * 2. render error - we should omit DESTROY_APP, make clean-up logic and start to listen for RENDER_APP action
     */
    const action = yield take([
      DESTROY_APP,
      FETCH_CONFIGURATION_FAIL,
      SERVICE_RENDER_ERROR,
    ]);

    if (action.type === DESTROY_APP) {
      // destroy app
      yield put(setIsRendered(false));
    }

    if (action.type === FETCH_CONFIGURATION_FAIL) {
      logger.error(
        'Failed to fetch configuration. Destroying service.\r\n',
        action.payload
      );
    }

    // TODO: notify host about service:render error

    yield cancel(appTask);
    yield cancel(serviceTask);

    yield call(resetThemeMode);
    // some cleanup logic for both cases DESTROY_APP and SERVICE_RENDER_ERROR
    yield put(resetStore());
    yield call(resetHistory);
    // we need some event about completion of the render flow
    yield put(destroyAppSuccess());
  }
}
